import { PALETTE_CRIMSON } from '@shared/builtinPulseMaps/palettes';
import { GUITAR_VARIANT_TUNING } from '@shared/builtinGuitarMaps/importedGuitarChart';
import { GOOD_WINDOW_MS, PERFECT_WINDOW_MS } from '@shared/gameTypes';
import type { GuitarChart } from '@shared/guitarChart';
import type { ChartAudio } from '@shared/chart';
import type { ManiaNote } from '@shared/maniaNote';

/**
 * Hand-crafted teaching chart for `/guitar/tutorial`. Four sections,
 * each anchored to a beat range so the React-side overlay text can
 * advance in sync with the chart:
 *
 *   beats  4-12  basic gem taps (alternating lanes)
 *   beats 13-21  hold gems (sustain ribbons)
 *   beats 22-28  chord gems (two lanes at once)
 *   beats 29-31  short freeplay tail before the "you've got it" card
 *
 * Audio reuses `my-heart.mp3` because at 100 BPM it's the slowest
 * Guitar audio shipped (the gentlest reading speed for a brand-new
 * player). Using an existing-shipped clip avoids adding a fresh
 * dedicated tutorial-only audio file purely for the warm-up; the
 * 32-beat tutorial finishes well inside the 60 s clip.
 *
 * Tuning copies the Normal variant of the imported set so a player
 * who clears the tutorial steps into the easiest tier at the same
 * approach speed they just trained on.
 */
const TUTORIAL_TOTAL_BEATS = 32;
const TUTORIAL_BPM = 100;

export function buildTutorialGuitarChart(): GuitarChart {
  const tuning = GUITAR_VARIANT_TUNING.normal;
  const audio: ChartAudio = {
    src: '/audio/guitar/my-heart.mp3',
    bpm: TUTORIAL_BPM,
    offsetMs: 0,
    durationSec: 60,
    attribution:
      'Tutorial uses "Heart Afire" by Defqwop (feat. Strix) [NCS Release] - https://ncs.io/HeartAfire',
    onsets: [],
  };

  const notes: ManiaNote[] = [
    // Section A — basic gem taps (overlay 1). Alternating lanes
    // across the row so the player practises every fret without
    // doubling up on any one side.
    { id: 't-01', beat: 4, lane: 1 },
    { id: 't-02', beat: 6, lane: 2 },
    { id: 't-03', beat: 8, lane: 0 },
    { id: 't-04', beat: 10, lane: 3 },

    // Section B — hold gems (overlay 2). Two-beat ribbons: long
    // enough to feel like a sustain, short enough that a slip is
    // easy to recover from. Lanes pulled from the inner pair so the
    // player practises the central frets first.
    { id: 'h-01', beat: 13, lane: 1, holdEndBeat: 15 },
    { id: 'h-02', beat: 17, lane: 2, holdEndBeat: 19 },

    // Section C — chord gems (overlay 3). One symmetric inner pair
    // (F+J) then one outer pair (D+K) so the player practises both
    // hand-spans before being asked to read a real chart.
    { id: 'd-01', beat: 22, lane: 1, dualPartnerLane: 2 },
    { id: 'd-02', beat: 25, lane: 0, dualPartnerLane: 3 },

    // Section D — freeplay tail (overlay 4). Two confidence-building
    // single taps before the "you're ready" card lands.
    { id: 't-05', beat: 28, lane: 2 },
    { id: 't-06', beat: 30, lane: 1 },
  ];

  return {
    mode: 'guitar',
    id: 'tutorial-guitar',
    slug: 'tutorial-guitar',
    title: 'How to play (Guitar)',
    description:
      'Quick teaching chart on a slow track. Strum the gems when they reach the bar.',
    bpm: TUTORIAL_BPM,
    difficulty: 1,
    variant: 'normal',
    totalBeats: TUTORIAL_TOTAL_BEATS,
    palette: PALETTE_CRIMSON,
    audio,
    notes,
    approachMs: tuning.approachMs,
    perfectWindowMs: PERFECT_WINDOW_MS * tuning.windowScale,
    goodWindowMs: GOOD_WINDOW_MS * tuning.windowScale,
  };
}

/**
 * Overlay instructions for the tutorial page, each anchored to a
 * beat range. Renderer in {@link import('../../pages/guitar/GuitarTutorialPage').GuitarTutorialPage}
 * shows the most recent overlay whose `fromBeat <= currentBeat <
 * untilBeat`.
 */
export interface GuitarTutorialOverlay {
  fromBeat: number;
  untilBeat: number;
  title: string;
  body: string;
}

/**
 * Returns the overlay sequence with input wording adapted to the
 * device. Touch devices get "tap on the lane" copy; keyboard devices
 * see both bindings (Guitar's primary DFJK row plus the QWER alt) so
 * first-time players know either hand position works.
 */
export function getGuitarTutorialOverlays(isTouch: boolean): GuitarTutorialOverlay[] {
  const tapVerb = isTouch ? 'Tap the lane' : 'Press D F J K (or Q W E R)';
  const holdVerb = isTouch ? 'Press and hold the lane' : 'Hold the key';
  return [
    {
      fromBeat: -8,
      untilBeat: 4,
      title: 'Strum each gem when it crosses the bar',
      body: `${tapVerb} the moment a gem touches the strum bar at the bottom. Four lanes, one key each.`,
    },
    {
      fromBeat: 4,
      untilBeat: 13,
      title: 'One strum per gem',
      body: 'A clean gem is a single strum. Catch each one as it lands on the bar — the closer to centre, the cleaner the score.',
    },
    {
      fromBeat: 13,
      untilBeat: 22,
      title: 'Hold gems trail a ribbon',
      body: `${holdVerb} from the moment the head crosses the bar, then release the second the top of the ribbon meets it.`,
    },
    {
      fromBeat: 22,
      untilBeat: 28,
      title: 'Chord gems light two lanes at once',
      body: 'When two gems arrive on the same beat, hit both keys together. A linked-pair stripe marks them for you.',
    },
    {
      fromBeat: 28,
      untilBeat: Number.POSITIVE_INFINITY,
      title: 'You\u2019ve got it',
      body: 'Free play to the end of the bar, then pick a real track from the home screen.',
    },
  ];
}
